import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { IGetPatientAdherenceDataResponse } from "../../../../@types/Treatments";
import {
  adherenceConfig,
  calculateYAxisDomain,
  opacityConfig,
} from "./AdherenceGraph.config";
import { CustomAdherenceRechartsTooltip } from "./components/CustomAdherenceRechartsTooltip/CustomAdherenceRechartsTooltip";

export type AdherenceCategory = (typeof adherenceConfig)[number]["type"];

export interface IAdherenceGraphProps {
  data: IGetPatientAdherenceDataResponse;
  onHoverCategory?: (type: string | null) => void;
  onSelectCategory?: (type: string | null) => void;
}

interface IAdherenceItem {
  label: string;
  value: number;
  fill: string;
  type: AdherenceCategory;
}

export function AdherenceGraph({
  data,
  onHoverCategory,
  onSelectCategory,
}: IAdherenceGraphProps) {
  const { t } = useTranslation();
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [graphData, setGraphData] = useState<IAdherenceItem[]>([]);

  useEffect(() => {
    const graphData: IAdherenceItem[] = adherenceConfig.map((config) => ({
      label: t(config.labelKey),
      value: config.getValue(data),
      fill: config.fill,
      type: config.type,
    }));

    setGraphData(graphData);
    setHoverIndex(null);
    setSelectedIndex(null);
    onHoverCategory?.(null);
    onSelectCategory?.(null);
  }, [data, t]);

  const handleBarSelect = useCallback(
    (_: IAdherenceItem, index: number) => {
      const nextIndex = selectedIndex === index ? null : index;
      setSelectedIndex(nextIndex);
      onSelectCategory?.(nextIndex === null ? null : graphData[nextIndex].type);
    },
    [graphData, onSelectCategory, selectedIndex]
  );

  const handleBarOpacity = (index: number) => {
    const isSelected = selectedIndex === index;
    const isHovered = hoverIndex === index;

    if (isSelected) return opacityConfig.selected;
    if (isHovered) return opacityConfig.hovered;
    if (selectedIndex !== null) return opacityConfig.dimmed;
    return opacityConfig.default;
  };

  const handleDomain = () => {
    return calculateYAxisDomain(data);
  };

  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
      maxHeight={370}
      style={{ marginTop: "6px", paddingTop: 48 }}
    >
      <BarChart
        data={graphData}
        margin={{ right: 50, top: 8, left: 30 }}
        barCategoryGap={5}
        data-testid="bar-chart"
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} z={0} />

        <Tooltip
          cursor={false}
          content={(props) => (
            <CustomAdherenceRechartsTooltip {...props} data={data} />
          )}
          wrapperStyle={{ outline: "none" }}
          isAnimationActive={false}
        />

        <XAxis
          dataKey="label"
          tickMargin={14}
          stroke="#D8D8D8"
          type="category"
          height={48}
          minTickGap={100}
          tick={{
            fill: "#727272",
            fontSize: "12px",
            fontWeight: "400",
          }}
        />

        <YAxis
          label={{
            value: t("dashboard_graph_patientsAxis"),
            angle: -90,
            position: "insideLeft",
            style: { fontSize: "12px", color: "#727272CC" },
          }}
          tickMargin={8}
          tickCount={10}
          stroke="#D8D8D8"
          domain={handleDomain()}
          width={48}
          tick={{
            fill: "#727272",
            fontSize: "12px",
            fontWeight: 400,
          }}
        />

        <Bar
          data-testid="bar"
          dataKey="value"
          onMouseEnter={(_, i) => {
            setHoverIndex((prev) => (prev === i ? prev : i));
            onHoverCategory?.(graphData[i]?.type ?? null);
          }}
          onMouseLeave={() => {
            setHoverIndex((prev) => (prev === null ? prev : null));
            onHoverCategory?.(null);
          }}
          onClick={(_, index) => handleBarSelect(graphData[index], index)}
          style={{ cursor: "pointer" }}
          isAnimationActive={false}
        >
          {graphData.map((d, i) => (
            <Cell
              data-testid="cell"
              key={`c-${i}`}
              fill={d.fill}
              fillOpacity={handleBarOpacity(i)}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
